import React, { useEffect, useMemo, useState } from 'react';
import { CirclePicker, ColorResult } from 'react-color';

interface Props {
	value?: string;
	defaultValue?: string;
	onChange?: (color: string) => void;
	disabled?: boolean;
	label?: string;
}

const HEX_REGEX = /^#([0-9A-F]{3}){1,2}$/i;

const normalizeHex = (val: string) => {
	if (!val) return '#7bb1ff';
	if (!val.startsWith('#')) val = `#${val}`;
	return val.slice(0, 7);
};

const ColorPicker: React.FC<Props> = ({
	value,
	defaultValue = '#7bb1ff',
	onChange,
	disabled,
	label,
}) => {
	const isControlled = value !== undefined;

	const [internalColor, setInternalColor] = useState<string>(
		normalizeHex(value ?? defaultValue),
	);

	useEffect(() => {
		if (isControlled && value) {
			setInternalColor(normalizeHex(value));
		}
	}, [value, isControlled]);

	const currentColor = isControlled ? normalizeHex(value!) : internalColor;

	const emitChange = (color: string) => {
		if (!HEX_REGEX.test(color)) return;

		if (!isControlled) {
			setInternalColor(color);
		}

		onChange?.(color);
	};

	const palette = useMemo(
		() => [
			'#7BB1FF',
			'#8ED1FC',
			'#0693E3',
			'#0052ea',
			'#00885a',
			'#00D084',
			'#7BDCB5',
			'#FCB900',
			'#FF6900',
			'#EB144C',
			'#F78DA7',
			'#ef00db',
			'#9900EF',
			'#6c00ef',
		],
		[],
	);

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 6,
				width: 'fit-content',
			}}>
			{label && (
				<label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>
			)}

			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				<div
					style={{
						opacity: disabled ? 0.6 : 1,
						pointerEvents: disabled ? 'none' : 'auto',
					}}>
					<CirclePicker
						color={currentColor}
						colors={palette}
						onChange={(c: ColorResult) => {
							emitChange(normalizeHex(c.hex));
						}}
						circleSize={22}
						circleSpacing={10}
					/>
				</div>
			</div>
		</div>
	);
};

export default ColorPicker;
